import { env } from '@percent/admin-dashboard/env'
import { POSTHOG_FEATURE_FLAGS, type PostHogFeatureFlag } from './posthogFeatureFlags'

/**
 * Development only: flag values for a dashboard with no PostHog - every local one, since
 * NX_PUBLIC_POSTHOG_API_KEY is only set for deployed builds. Without PostHog every flag is off, so
 * a view that ships behind a flag cannot be used or tested locally at all.
 *
 * Two sources, both JSON objects of flag key to boolean such as `{"ad_roles_view":true}`:
 *   - NX_PUBLIC_POSTHOG_FLAG_OVERRIDES, the stack's defaults (.env.development) for people;
 *   - localStorage `posthogFlagOverrides`, per browser, which wins - the e2e suites' flag helper
 *     sets it, so a test chooses its own flags locally as it does against PostHog when deployed.
 * They apply only to a development build with no PostHog configured: where PostHog answers, it is
 * the only answer. See `developmentFlagOverrides` below for how no other build carries them.
 */
export type FlagOverrides = Partial<Record<PostHogFeatureFlag, boolean>>

const NO_OVERRIDES: FlagOverrides = Object.freeze({})

const KNOWN_FLAGS = new Set<string>(Object.values(POSTHOG_FEATURE_FLAGS))

export const parseFlagOverrides = (raw: string | null | undefined): FlagOverrides => {
  if (!raw) {
    return NO_OVERRIDES
  }

  const parsed: unknown = JSON.parse(raw)

  if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) {
    throw new Error('NX_PUBLIC_POSTHOG_FLAG_OVERRIDES must be a JSON object of flag key to boolean')
  }

  return Object.fromEntries(
    Object.entries(parsed).map(([flag, value]) => {
      if (!KNOWN_FLAGS.has(flag)) {
        throw new Error(`NX_PUBLIC_POSTHOG_FLAG_OVERRIDES names an unknown flag: ${flag}`)
      }

      if (typeof value !== 'boolean') {
        throw new Error(`NX_PUBLIC_POSTHOG_FLAG_OVERRIDES: ${flag} must be true or false`)
      }

      return [flag, value]
    })
  )
}

export const LOCAL_FLAG_OVERRIDES_KEY = 'posthogFlagOverrides'

// The browser's overrides come from the e2e suites' flag helper, which sets every flag a test names -
// the api's among them - or are stale. Each entry this app does not know is skipped on its own, so
// one cannot cost a test the others, and anything unreadable is ignored rather than breaking the app.
const browserOverrides = (stored: string | null | undefined): FlagOverrides => {
  let parsed: unknown
  try {
    parsed = JSON.parse(stored || '{}')
  } catch {
    return NO_OVERRIDES
  }

  if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) {
    return NO_OVERRIDES
  }

  return Object.fromEntries(
    Object.entries(parsed).filter(([flag, value]) => KNOWN_FLAGS.has(flag) && typeof value === 'boolean')
  )
}

/** Both sources merged, the browser's winning. */
export const resolveFlagOverrides = ({
  stored,
  configured
}: {
  stored?: string | null
  configured?: string
}): FlagOverrides =>
  // The stack's is strict: a typo in .env.development should be loud.
  ({ ...parseFlagOverrides(configured), ...browserOverrides(stored) })

/** Every flag the overrides turn on. */
export const flagsTurnedOn = (overrides: FlagOverrides): PostHogFeatureFlag[] =>
  Object.values(POSTHOG_FEATURE_FLAGS).filter(flag => overrides[flag] === true)

const readStoredOverrides = (): string | null => {
  try {
    return window.localStorage.getItem(LOCAL_FLAG_OVERRIDES_KEY)
  } catch {
    return null
  }
}

// Every flag hook reads the overrides on every render, and the raw strings rarely change: parse a
// pair once, and hand back the same object until either string does.
let lastResolved: { stored: string | null; configured?: string; overrides: FlagOverrides } | undefined

const resolveOnce = (stored: string | null, configured: string | undefined): FlagOverrides => {
  if (lastResolved?.stored !== stored || lastResolved.configured !== configured) {
    lastResolved = { stored, configured, overrides: resolveFlagOverrides({ stored, configured }) }
  }

  return lastResolved.overrides
}

/**
 * The overrides in force - none, unless this is a development build with no PostHog configured.
 *
 * The build check is decided when the bundle is built, not when it runs: the bundler replaces
 * `process.env.NODE_ENV` with the build mode - `development` only from the dev server, `production`
 * from every deployed build - so in any other build the test below is `true` from the start, and the
 * minifier drops everything after it - no deployed bundle carries the override code, not merely
 * skips it, whatever its configuration says. Keep the check in this form (not through `env`) for that.
 */
export const developmentFlagOverrides = (): FlagOverrides => {
  // eslint-disable-next-line no-restricted-syntax -- the build-time literal described above; `env` is read at runtime
  if (process.env.NODE_ENV !== 'development' || env.NX_PUBLIC_POSTHOG_API_KEY) {
    return NO_OVERRIDES
  }

  return resolveOnce(readStoredOverrides(), env.NX_PUBLIC_POSTHOG_FLAG_OVERRIDES)
}
